// src/pages/Jampot2026.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  FaBed,
  FaGasPump,
  FaInfoCircle,
  FaMapMarkedAlt,
  FaRoute,
  FaTimes,
  FaUtensils,
} from "react-icons/fa";
import "./Jampot2026.css";

import rallyBadge from "../../assets/rallyBadge.png";
import map from "../../assets/grounds_map.png";
import mtkelly from "../../assets/mtkelly.jpg";
import appPreview from "../../assets/app.png";

// Scenery photos
import dartmoor1 from "../../assets/Jampot/dartmoor1.jpeg";
import dartmoor2 from "../../assets/Jampot/dartmoor2.jpeg";
import braunton1 from "../../assets/Jampot/braunton.avif";
import braunton2 from "../../assets/Jampot/braunton2.avif";
import tintagel1 from "../../assets/Jampot/tintagel.png";
import tintagel2 from "../../assets/Jampot/tintagel2.png";
import heroimage from "../../assets/Jampot/hero5.avif";
import rallyShirts from "../../assets/Jampot/rally-shirts-2026.png";
import wingLeft from "../../assets/Jampot/wing_left.png";
import wingRight from "../../assets/Jampot/wing_right.png";

import floorGround from "../../assets/floor_ground 2.svg";
import floor1 from "../../assets/floor_1 2.svg";
import floor2 from "../../assets/floor_2 2.svg";
import RallyStatsRow from "./RallyStatsRow";
import RoomsBookedBar from "./RoomsBookedBar";
import RallyMenu from "./MenuSchedule";

const bookingOpen = false;
const iosAppStoreUrl = "https://apps.apple.com/gb/app/jampot-rally/id6757719104";
const androidApkDownloadUrl = "/rallies/2026/app/jampot-rally-android.apk";
const routeViewBox = { width: 640, height: 360, padding: 42 };
const routeTileSize = 256;
const routeZoomRange = { min: 8, max: 12 };
const routeInteractiveZoomRange = { min: 0, max: 5 };
const maxMercatorLat = 85.05112878;
const routeDirections = [
  { key: "out", label: "Out", field: "out" },
  { key: "back", label: "Back", field: "back" },
];
const maxGpxPreviewPoints = 1400;
const maxElevationProfilePoints = 180;
const metresPerMile = 1609.344;

function isRoutePoint(point) {
  return Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng));
}

function normaliseRoutePoint(point) {
  const ele = Number(point.ele);

  return {
    ...point,
    lat: Number(point.lat),
    lng: Number(point.lng),
    ...(Number.isFinite(ele) ? { ele } : {}),
  };
}

function getRouteLegPoints(leg) {
  if (!leg?.start || !leg?.end) return [];

  return [leg.start, ...(leg.waypoints ?? []), leg.end]
    .filter(isRoutePoint)
    .map(normaliseRoutePoint);
}

function getRouteLegs(navigation) {
  return routeDirections
    .map((direction) => {
      const leg = navigation?.[direction.field];
      const points = getRouteLegPoints(leg);

      return {
        key: direction.key,
        label: direction.label,
        title: leg?.label ?? direction.label,
        points,
        url: buildGoogleMapsDirectionsUrl(leg),
      };
    })
    .filter((leg) => leg.points.length >= 2);
}

function buildGoogleMapsDirectionsUrl(leg) {
  const points = getRouteLegPoints(leg);
  if (points.length < 2) return "";

  const formatPoint = (point) => `${point.lat},${point.lng}`;
  const params = new URLSearchParams({
    api: "1",
    origin: formatPoint(points[0]),
    destination: formatPoint(points[points.length - 1]),
    travelmode: "driving",
  });

  const waypoints = points.slice(1, -1).map(formatPoint);
  if (waypoints.length > 0) {
    params.set("waypoints", waypoints.join("|"));
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function latLngToWorldPixel(point, zoom) {
  const lat = clamp(point.lat, -maxMercatorLat, maxMercatorLat);
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const scale = routeTileSize * 2 ** zoom;

  return {
    x: ((point.lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
}

function chooseRouteZoom(legs) {
  const points = legs.flatMap((leg) => leg.points);

  for (let zoom = routeZoomRange.max; zoom >= routeZoomRange.min; zoom -= 1) {
    const worldPoints = points.map((point) => latLngToWorldPixel(point, zoom));
    const xs = worldPoints.map((point) => point.x);
    const ys = worldPoints.map((point) => point.y);
    const spanX = Math.max(...xs) - Math.min(...xs);
    const spanY = Math.max(...ys) - Math.min(...ys);

    if (
      spanX <= routeViewBox.width - routeViewBox.padding * 2 &&
      spanY <= routeViewBox.height - routeViewBox.padding * 2
    ) {
      return zoom;
    }
  }

  return routeZoomRange.min;
}

function getRouteMapModel(legs, zoomLevel = 0, pan = { x: 0, y: 0 }) {
  const points = legs.flatMap((leg) => leg.points);
  const zoom = clamp(
    chooseRouteZoom(legs) + zoomLevel,
    routeZoomRange.min,
    routeZoomRange.max + routeInteractiveZoomRange.max
  );
  const worldPoints = points.map((point) => latLngToWorldPixel(point, zoom));
  const xs = worldPoints.map((point) => point.x);
  const ys = worldPoints.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const originX = (minX + maxX) / 2 - routeViewBox.width / 2 - pan.x;
  const originY = (minY + maxY) / 2 - routeViewBox.height / 2 - pan.y;
  const firstTileX = Math.floor(originX / routeTileSize);
  const lastTileX = Math.floor((originX + routeViewBox.width) / routeTileSize);
  const firstTileY = Math.floor(originY / routeTileSize);
  const lastTileY = Math.floor((originY + routeViewBox.height) / routeTileSize);
  const maxTile = 2 ** zoom;
  const tiles = [];

  for (let tileX = firstTileX; tileX <= lastTileX; tileX += 1) {
    for (let tileY = firstTileY; tileY <= lastTileY; tileY += 1) {
      if (tileY < 0 || tileY >= maxTile) continue;

      const wrappedTileX = ((tileX % maxTile) + maxTile) % maxTile;

      tiles.push({
        key: `${zoom}-${tileX}-${tileY}`,
        href: `https://tile.openstreetmap.org/${zoom}/${wrappedTileX}/${tileY}.png`,
        x: tileX * routeTileSize - originX,
        y: tileY * routeTileSize - originY,
      });
    }
  }

  return {
    originX,
    originY,
    tiles,
    zoom,
  };
}

function projectRoutePoint(point, mapModel) {
  const worldPoint = latLngToWorldPixel(point, mapModel.zoom);

  return {
    x: worldPoint.x - mapModel.originX,
    y: worldPoint.y - mapModel.originY,
  };
}

function getRoutePath(points, mapModel) {
  return points
    .map((point, index) => {
      const { x, y } = projectRoutePoint(point, mapModel);
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

function getRouteEndpoints(legs) {
  const seen = new Set();

  return legs.flatMap((leg) => [leg.points[0], leg.points[leg.points.length - 1]]).filter((point) => {
    const key = `${point.lat.toFixed(5)}-${point.lng.toFixed(5)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getXmlElements(parent, tagName) {
  return Array.from(parent.getElementsByTagNameNS("*", tagName));
}

function getXmlChildText(parent, tagName) {
  return getXmlElements(parent, tagName)[0]?.textContent?.trim() ?? "";
}

function getGpxPoints(parent, tagName) {
  return getXmlElements(parent, tagName)
    .map((point) => {
      const lng = point.getAttribute("lon") ?? point.getAttribute("lng");
      const ele = Number(getXmlChildText(point, "ele"));

      return {
        lat: Number(point.getAttribute("lat")),
        lng: lng === null ? NaN : Number(lng),
        ...(Number.isFinite(ele) ? { ele } : {}),
        name: getXmlChildText(point, "name") || undefined,
      };
    })
    .filter(isRoutePoint)
    .map(normaliseRoutePoint);
}

function getDistanceMetres(a, b) {
  const earthRadiusMetres = 6371000;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const deltaLat = toRadians(b.lat - a.lat);
  const deltaLng = toRadians(b.lng - a.lng);
  const h =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;

  return 2 * earthRadiusMetres * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function downsampleRoutePoints(points, maxPoints = maxGpxPreviewPoints) {
  if (points.length <= maxPoints) return points;

  const sampled = [];
  const step = (points.length - 1) / (maxPoints - 1);
  let previousIndex = -1;

  for (let i = 0; i < maxPoints; i += 1) {
    const index = Math.round(i * step);
    if (index === previousIndex) continue;
    sampled.push(points[index]);
    previousIndex = index;
  }

  return sampled;
}

function buildElevationProfile(pointSets) {
  const elevationPoints = [];
  let distanceMetres = 0;
  let previousRoutePoint = null;
  let previousElevation = null;
  let totalAscentMetres = 0;
  let totalDescentMetres = 0;

  pointSets.forEach((points) => {
    points.forEach((point) => {
      if (previousRoutePoint) {
        distanceMetres += getDistanceMetres(previousRoutePoint, point);
      }

      if (Number.isFinite(point.ele)) {
        if (previousElevation !== null) {
          const change = point.ele - previousElevation;

          if (change > 0) {
            totalAscentMetres += change;
          } else {
            totalDescentMetres += Math.abs(change);
          }
        }

        elevationPoints.push({
          distanceMetres,
          elevationMetres: point.ele,
        });
        previousElevation = point.ele;
      }

      previousRoutePoint = point;
    });
  });

  if (elevationPoints.length < 2) return null;

  const elevations = elevationPoints.map((point) => point.elevationMetres);
  const minElevationMetres = Math.min(...elevations);
  const maxElevationMetres = Math.max(...elevations);
  const displayPoints = downsampleRoutePoints(elevationPoints, maxElevationProfilePoints);

  return {
    points: displayPoints,
    rawPointCount: elevationPoints.length,
    totalDistanceMetres: distanceMetres,
    minElevationMetres,
    maxElevationMetres,
    totalAscentMetres,
    totalDescentMetres,
  };
}

function parseGpxRoute(xmlText) {
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const parserErrors = doc.getElementsByTagName("parsererror");

  if (parserErrors.length > 0) {
    throw new Error("The GPX file could not be parsed.");
  }

  let totalPoints = 0;
  const legs = [];
  const elevationPointSets = [];

  getXmlElements(doc, "trk").forEach((track, trackIndex) => {
    const trackName = getXmlChildText(track, "name") || "GPX track";

    getXmlElements(track, "trkseg").forEach((segment, segmentIndex) => {
      const points = getGpxPoints(segment, "trkpt");
      totalPoints += points.length;

      if (points.length >= 2) {
        elevationPointSets.push(points);
        legs.push({
          key: `gpx-${trackIndex}-${segmentIndex}`,
          label: trackName,
          title: trackName,
          points: downsampleRoutePoints(points),
          rawPointCount: points.length,
        });
      }
    });
  });

  if (legs.length === 0) {
    getXmlElements(doc, "rte").forEach((route, routeIndex) => {
      const routeName = getXmlChildText(route, "name") || "GPX route";
      const points = getGpxPoints(route, "rtept");
      totalPoints += points.length;

      if (points.length >= 2) {
        elevationPointSets.push(points);
        legs.push({
          key: `gpx-route-${routeIndex}`,
          label: routeName,
          title: routeName,
          points: downsampleRoutePoints(points),
          rawPointCount: points.length,
        });
      }
    });
  }

  if (legs.length === 0) {
    throw new Error("The GPX file does not contain a displayable route.");
  }

  return {
    elevationProfile: buildElevationProfile(elevationPointSets),
    legs,
    previewPoints: legs.reduce((total, leg) => total + leg.points.length, 0),
    totalPoints,
  };
}

function useGpxRoute(downloadHref) {
  const [route, setRoute] = useState({
    error: "",
    elevationProfile: null,
    legs: [],
    loading: Boolean(downloadHref),
    previewPoints: 0,
    totalPoints: 0,
  });

  useEffect(() => {
    if (!downloadHref) {
      setRoute({
        error: "",
        elevationProfile: null,
        legs: [],
        loading: false,
        previewPoints: 0,
        totalPoints: 0,
      });
      return undefined;
    }

    const controller = new AbortController();

    setRoute((currentRoute) => ({
      ...currentRoute,
      error: "",
      loading: true,
    }));

    fetch(downloadHref, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error("The GPX file could not be loaded.");
        }
        return response.text();
      })
      .then((xmlText) => {
        const parsedRoute = parseGpxRoute(xmlText);
        setRoute({
          ...parsedRoute,
          error: "",
          loading: false,
        });
      })
      .catch((error) => {
        if (error.name === "AbortError") return;

        setRoute({
          error: error.message || "The GPX route could not be displayed.",
          elevationProfile: null,
          legs: [],
          loading: false,
          previewPoints: 0,
          totalPoints: 0,
        });
      });

    return () => controller.abort();
  }, [downloadHref]);

  return route;
}

function addGpxEndpointLabels(gpxLegs, fallbackLegs) {
  if (gpxLegs.length === 0) return gpxLegs;

  const fallbackPoints = fallbackLegs.flatMap((leg) => leg.points);
  const startName = fallbackPoints[0]?.name ?? "Start";
  const finishName = fallbackPoints[fallbackPoints.length - 1]?.name ?? "Finish";

  return gpxLegs.map((leg, legIndex) => ({
    ...leg,
    points: leg.points.map((point, pointIndex) => {
      if (legIndex === 0 && pointIndex === 0) {
        return { ...point, name: point.name ?? startName };
      }

      if (legIndex === gpxLegs.length - 1 && pointIndex === leg.points.length - 1) {
        return { ...point, name: point.name ?? finishName };
      }

      return point;
    }),
  }));
}

function formatMiles(metres) {
  return `${(Number(metres || 0) / metresPerMile).toFixed(1)} miles`;
}

function formatElevationMetres(metres) {
  return `${Math.round(Number(metres || 0)).toLocaleString()} m`;
}

function ElevationProfile({ profile, rideTitle }) {
  if (!profile?.points?.length || profile.points.length < 2) return null;

  const chart = {
    width: 640,
    height: 190,
    paddingLeft: 42,
    paddingRight: 18,
    paddingTop: 20,
    paddingBottom: 34,
  };
  const plotWidth = chart.width - chart.paddingLeft - chart.paddingRight;
  const plotHeight = chart.height - chart.paddingTop - chart.paddingBottom;
  const elevationRange = Math.max(
    1,
    profile.maxElevationMetres - profile.minElevationMetres
  );
  const totalDistance = Math.max(1, profile.totalDistanceMetres);
  const points = profile.points.map((point) => {
    const x =
      chart.paddingLeft +
      (point.distanceMetres / totalDistance) * plotWidth;
    const y =
      chart.paddingTop +
      (1 -
        (point.elevationMetres - profile.minElevationMetres) / elevationRange) *
        plotHeight;

    return { x, y };
  });
  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${
    chart.height - chart.paddingBottom
  } L ${points[0].x.toFixed(1)} ${chart.height - chart.paddingBottom} Z`;
  const baseY = chart.height - chart.paddingBottom;

  return (
    <div className="rideout-elevation-profile">
      <div className="rideout-elevation-profile__header">
        <div>
          <h5>Altitude Profile</h5>
          <p>Elevation from the downloadable GPX route.</p>
        </div>
        <span>{formatMiles(profile.totalDistanceMetres)}</span>
      </div>

      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        role="img"
        aria-label={`${rideTitle} altitude profile from GPX elevation data`}
        preserveAspectRatio="xMidYMid meet"
      >
        <title>{rideTitle} altitude profile</title>
        <desc>
          Elevation profile showing altitude over distance, from
          {` ${formatElevationMetres(profile.minElevationMetres)} to ${formatElevationMetres(profile.maxElevationMetres)}.`}
        </desc>
        <line
          className="rideout-elevation-profile__grid"
          x1={chart.paddingLeft}
          x2={chart.width - chart.paddingRight}
          y1={chart.paddingTop}
          y2={chart.paddingTop}
        />
        <line
          className="rideout-elevation-profile__axis"
          x1={chart.paddingLeft}
          x2={chart.width - chart.paddingRight}
          y1={baseY}
          y2={baseY}
        />
        <path className="rideout-elevation-profile__area" d={areaPath} />
        <path className="rideout-elevation-profile__line" d={linePath} />
        <text x={8} y={chart.paddingTop + 4}>
          {formatElevationMetres(profile.maxElevationMetres)}
        </text>
        <text x={8} y={baseY + 4}>
          {formatElevationMetres(profile.minElevationMetres)}
        </text>
        <text x={chart.paddingLeft} y={chart.height - 9}>
          0 miles
        </text>
        <text x={chart.width - chart.paddingRight} y={chart.height - 9} textAnchor="end">
          {formatMiles(profile.totalDistanceMetres)}
        </text>
      </svg>

      <div className="rideout-elevation-profile__stats">
        <span>
          <strong>High</strong>
          {formatElevationMetres(profile.maxElevationMetres)}
        </span>
        <span>
          <strong>Low</strong>
          {formatElevationMetres(profile.minElevationMetres)}
        </span>
        <span>
          <strong>Climb</strong>
          {formatElevationMetres(profile.totalAscentMetres)}
        </span>
        <span>
          <strong>Descent</strong>
          {formatElevationMetres(profile.totalDescentMetres)}
        </span>
      </div>
    </div>
  );
}

function RideRouteMap({ ride }) {
  const primaryDownload = (ride.downloads ?? []).find((download) => download.href);
  const primaryDownloadHref = primaryDownload?.href;
  const fallbackLegs = getRouteLegs(ride.navigation);
  const gpxRoute = useGpxRoute(primaryDownloadHref);
  const mapSvgRef = useRef(null);
  const [routeZoom, setRouteZoom] = useState(0);
  const [routePan, setRoutePan] = useState({ x: 0, y: 0 });
  const [routeDrag, setRouteDrag] = useState(null);
  const usesGpxTrack = gpxRoute.legs.length > 0;
  const legs = usesGpxTrack ? addGpxEndpointLabels(gpxRoute.legs, fallbackLegs) : fallbackLegs;
  const showGoogleMapActions = !primaryDownload && !usesGpxTrack;

  useEffect(() => {
    setRouteZoom(0);
    setRoutePan({ x: 0, y: 0 });
    setRouteDrag(null);
  }, [primaryDownloadHref, ride.title]);

  if (!primaryDownload && legs.length === 0) return null;

  const routeActions = (
    <div className="rideout-route-actions">
      {(ride.downloads ?? []).map((download) => (
        <a
          key={download.href}
          className="rideout-route-link rideout-route-link-download"
          href={download.href}
          download={download.filename || true}
        >
          {download.label || "Download GPX"}
        </a>
      ))}

      {showGoogleMapActions &&
        legs.map((leg) => (
          <a
            key={leg.key}
            className={`rideout-route-link rideout-route-link-${leg.key}`}
            href={leg.url}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open ${leg.title} for ${ride.title} in Google Maps`}
          >
            Open {leg.label} in Google Maps
          </a>
        ))}
    </div>
  );

  if (gpxRoute.loading) {
    return (
      <div className="rideout-route">
        <div className="rideout-route-header">
          <div>
            <h4>GPX Route Preview</h4>
            <p>Loading the GPX track from the downloadable route file.</p>
          </div>
        </div>

        <div className="rideout-route-map rideout-route-map-status" role="status">
          Loading GPX route...
        </div>

        {routeActions}
      </div>
    );
  }

  if (legs.length === 0) {
    return (
      <div className="rideout-route">
        <div className="rideout-route-header">
          <div>
            <h4>GPX Route Preview</h4>
            <p>{gpxRoute.error || "The route preview is not available yet."}</p>
          </div>
        </div>

        {routeActions}
      </div>
    );
  }

  const endpoints = getRouteEndpoints(legs);
  const { width, height } = routeViewBox;
  const mapModel = getRouteMapModel(legs, routeZoom, routePan);
  const canZoomIn = routeZoom < routeInteractiveZoomRange.max;
  const canZoomOut = routeZoom > routeInteractiveZoomRange.min;
  const isZoomed = routeZoom > routeInteractiveZoomRange.min || routePan.x !== 0 || routePan.y !== 0;

  const getSvgPointerPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();

    return {
      x: ((event.clientX - rect.left) / rect.width) * width,
      y: ((event.clientY - rect.top) / rect.height) * height,
    };
  };

  const zoomRoute = (amount) => {
    setRouteZoom((currentZoom) =>
      clamp(
        currentZoom + amount,
        routeInteractiveZoomRange.min,
        routeInteractiveZoomRange.max
      )
    );
  };

  const resetRouteView = () => {
    setRouteZoom(0);
    setRoutePan({ x: 0, y: 0 });
    setRouteDrag(null);
  };

  const handleMapPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    const pointer = getSvgPointerPosition(event);
    setRouteDrag({
      pointerId: event.pointerId,
      startPointer: pointer,
      startPan: routePan,
    });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleMapPointerMove = (event) => {
    if (!routeDrag || routeDrag.pointerId !== event.pointerId) return;

    const pointer = getSvgPointerPosition(event);
    setRoutePan({
      x: routeDrag.startPan.x + pointer.x - routeDrag.startPointer.x,
      y: routeDrag.startPan.y + pointer.y - routeDrag.startPointer.y,
    });
  };

  const handleMapPointerUp = (event) => {
    if (!routeDrag || routeDrag.pointerId !== event.pointerId) return;

    setRouteDrag(null);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleMapWheel = (event) => {
    event.preventDefault();
    zoomRoute(event.deltaY < 0 ? 1 : -1);
  };

  return (
    <div className="rideout-route">
      <div className="rideout-route-header">
        <div>
          <h4>{usesGpxTrack ? "GPX Route Preview" : "Route Map"}</h4>
          <p>
            {usesGpxTrack
              ? "Actual GPX track plotted over live map tiles."
              : "Published satnav points plotted over live map tiles."}
          </p>
        </div>

        <div className="rideout-route-legend" aria-label="Route legend">
          {usesGpxTrack ? (
            <span className="rideout-legend-gpx">GPX track</span>
          ) : (
            <>
              <span className="rideout-legend-out">Out</span>
              <span className="rideout-legend-back">Back</span>
            </>
          )}
        </div>
      </div>

      <div className="rideout-route-map">
        <div className="rideout-route-map-controls" aria-label="Route map controls">
          <button
            type="button"
            onClick={() => zoomRoute(1)}
            disabled={!canZoomIn}
            aria-label="Zoom route map in"
            title="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => zoomRoute(-1)}
            disabled={!canZoomOut}
            aria-label="Zoom route map out"
            title="Zoom out"
          >
            -
          </button>
          <button
            type="button"
            onClick={resetRouteView}
            disabled={!isZoomed}
            aria-label="Reset route map view"
            title="Reset view"
          >
            Reset
          </button>
        </div>

        <svg
          ref={mapSvgRef}
          className={routeDrag ? "rideout-route-map-svg is-dragging" : "rideout-route-map-svg"}
          viewBox={`0 0 ${width} ${height}`}
          role="application"
          aria-label={`${ride.title} route preview. Use the map buttons to zoom, then drag the map to pan.`}
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={handleMapPointerDown}
          onPointerMove={handleMapPointerMove}
          onPointerUp={handleMapPointerUp}
          onPointerCancel={handleMapPointerUp}
          onWheel={handleMapWheel}
        >
          <title>{ride.title} route preview</title>
          <desc>
            {usesGpxTrack
              ? "Route line based on the downloadable GPX file."
              : "Outbound and return route lines based on the ride-out navigation coordinates."}
          </desc>
          <rect width={width} height={height} className="rideout-map-tile-fallback" />

          {mapModel.tiles.map((tile) => (
            <image
              key={tile.key}
              className="rideout-map-tile"
              href={tile.href}
              x={tile.x}
              y={tile.y}
              width={routeTileSize}
              height={routeTileSize}
              preserveAspectRatio="none"
            />
          ))}

          {legs.map((leg) => (
            <g key={leg.key}>
              <path
                className={`rideout-route-line ${
                  usesGpxTrack ? "rideout-route-line-gpx" : `rideout-route-line-${leg.key}`
                }`}
                d={getRoutePath(leg.points, mapModel)}
              />

              {!usesGpxTrack &&
                leg.points.slice(1, -1).map((point, index) => {
                  const { x, y } = projectRoutePoint(point, mapModel);

                  return (
                    <circle
                      key={`${leg.key}-${point.lat}-${point.lng}-${index}`}
                      className={`rideout-route-waypoint rideout-route-waypoint-${leg.key}`}
                      cx={x}
                      cy={y}
                      r="4"
                    />
                  );
                })}
            </g>
          ))}

          {endpoints.map((point) => {
            const { x, y } = projectRoutePoint(point, mapModel);
            const textAnchor = x > width / 2 ? "end" : "start";
            const labelX = x > width / 2 ? x - 13 : x + 13;
            const labelY = y > height - 54 ? y - 13 : y + 5;

            return (
              <g key={`${point.lat}-${point.lng}`} className="rideout-route-marker-group">
                <circle className="rideout-route-marker" cx={x} cy={y} r="7" />
                <text
                  className="rideout-route-label"
                  x={labelX}
                  y={labelY}
                  textAnchor={textAnchor}
                >
                  {point.name ?? "Stop"}
                </text>
              </g>
            );
          })}
        </svg>

        <a
          className="rideout-map-attribution"
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          © OpenStreetMap contributors
        </a>
      </div>

      {usesGpxTrack ? (
        <ElevationProfile profile={gpxRoute.elevationProfile} rideTitle={ride.title} />
      ) : null}

      <div className="rideout-route-summary">
        {usesGpxTrack ? (
          <span>
            <strong>GPX track:</strong> {gpxRoute.totalPoints.toLocaleString()} route points
            displayed from {primaryDownload?.filename ?? "the GPX file"}
            {gpxRoute.previewPoints < gpxRoute.totalPoints
              ? ` (${gpxRoute.previewPoints.toLocaleString()} points used for the preview)`
              : ""}
          </span>
        ) : (
          legs.map((leg) => (
            <span key={leg.key}>
              <strong>{leg.label}:</strong> {leg.points[0].name ?? "Start"} to{" "}
              {leg.points[leg.points.length - 1].name ?? "Finish"}
            </span>
          ))
        )}
      </div>

      {routeActions}
    </div>
  );
}

function PhotoStrip({ images = [] }) {
  return (
    <div className="jp-photo-strip" aria-label="Devon & Cornwall scenery photos">
      {images.map((img) => (
        <figure key={img.src} className="jp-photo">
          <img src={img.src} alt={img.alt} loading="lazy" />
          {img.caption ? <figcaption>{img.caption}</figcaption> : null}
        </figure>
      ))}
    </div>
  );
}

function Lightbox({ open, src, alt, onClose }) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="jp-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <button className="jp-lightbox-close" onClick={onClose} aria-label="Close">
        ×
      </button>

      <div className="jp-lightbox-inner" onClick={(e) => e.stopPropagation()}>
        <img className="jp-lightbox-img" src={src} alt={alt} />
      </div>
    </div>
  );
}

function BoardingRoomPriceNotice({ compact = false }) {
  return (
    <div
      className={compact ? "jp-room-price-notice compact" : "jp-room-price-notice"}
      role="note"
      aria-label="Boarding room price reduction"
    >
      <strong>Last 3 boarding house rooms reduced in price.</strong>{" "}
      Book soon if you would like on-site accommodation at the rally.
    </div>
  );
}

function GettingHerePanel() {
  return (
    <section className="jp-section jp-arrival-panel" id="getting-here">
      <div className="jp-arrival-panel__heading">
        <div>
          <h2>Address &amp; Arrival</h2>
          <p>
            Please arrive at the <strong>camping field entrance</strong>. Rally reception will be based
            there for check-in.
          </p>
        </div>
      </div>

      <div className="jp-arrival-grid">
        <div className="jp-arrival-card jp-arrival-card--primary">
          <span className="jp-arrival-card__label">Use this entrance for arrival</span>
          <h3>Camping field &amp; rally reception</h3>
          <p>
            <strong>what3words:</strong>{" "}
            <a href="https://what3words.com/engage.soda.solve" target="_blank" rel="noreferrer">
              ///engage.soda.solve
            </a>
          </p>
          <p>
            All rally-goers should head here first, whether camping, staying in rooms, or arriving
            just for check-in.
          </p>
        </div>

        <div className="jp-arrival-card">
          <span className="jp-arrival-card__label">Main site entrance</span>
          <h3>Mount Kelly main site</h3>
          <p>
            <strong>what3words:</strong>{" "}
            <a href="https://what3words.com/best.sparks.tuck" target="_blank" rel="noreferrer">
              ///best.sparks.tuck
            </a>
          </p>
          <p>Use this as a reference for the main school site, not as the rally check-in point.</p>
        </div>
      </div>

      <div className="jp-reception-times" aria-label="Rally reception opening times">
        <h3>Reception opening times</h3>
        <dl>
          {receptionOpeningTimes.map(({ day, time }) => (
            <div key={day}>
              <dt>{day}</dt>
              <dd>{time}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="jp-arrival-address">
        <strong>Venue address:</strong> Mount Kelly College, Parkwood Road, Tavistock, Devon, PL19 0HZ.
      </div>
    </section>
  );
}

function flattenProgrammeEvents(programme) {
  return (programme?.days ?? []).flatMap((day) =>
    (day.events ?? []).map((event) => ({
      ...event,
      dayLabel: day.label,
    }))
  );
}

function getInfoItemsByTitle(info, titles) {
  const wanted = new Set(titles.map((title) => title.toLowerCase()));

  return (info?.sections ?? [])
    .flatMap((section) => section.items ?? [])
    .filter((item) => wanted.has(String(item.title || "").toLowerCase()));
}

function actionUri(uri) {
  if (!uri) return "";
  if (uri.startsWith("map:")) {
    return `https://maps.google.com/?q=${encodeURIComponent(uri.slice(4))}`;
  }

  return uri;
}

const receptionOpeningTimes = [
  { day: "Thursday", time: "12pm-5pm" },
  { day: "Friday", time: "9am-5pm" },
  { day: "Saturday", time: "9am-5pm" },
  { day: "Sunday", time: "9am-12pm" },
  { day: "Monday", time: "Closed" },
];

const receptionOpeningRows = receptionOpeningTimes.map(
  ({ day, time }) => `${day}: ${time}`
);
const rallyTabTitles = {
  Venue: {
    title: "Venue",
    subtitle: "Mount Kelly. Camping. Rooms. Arrival details.",
  },
  Events: {
    title: "Events",
    subtitle: "Programme times. Entertainment. Weekend schedule.",
  },
  RideOuts: {
    title: "Ride Outs",
    subtitle: "Ride-outs for the rally weekend.",
  },
  Extras: {
    title: "Extras",
    subtitle: "Local services. Contacts. Rally companion app.",
  },
};

function buildQuickInfoItems({ programme, ridesData, rallyInfo }) {
  const events = flattenProgrammeEvents(programme);
  const foodEvents = events.filter((event) => event.tag === "FOOD");
  const rideEvents = events.filter((event) => event.tag === "RIDE OUT");
  const serviceItems = getInfoItemsByTitle(rallyInfo, [
    "Petrol",
    "Classic Bike Spares/Repairs",
    "Toilets",
    "Showers",
    "Parking",
  ]);
  const contactItems = getInfoItemsByTitle(rallyInfo, ["Rally Coordinator"]);
  const serviceRows = serviceItems.length
    ? serviceItems.map((item) => `${item.title}: ${item.detail}`)
    : [
        "Petrol: for E5 / super unleaded, use Morrisons Tavistock. Texaco is also nearby for standard fuel.",
        "Classic bike support: Montys Classic Motorcycles and Colin Flashman Motorcycles are listed in Extras.",
        "Toilets and showers will be available on the camping field.",
        "Parking is available on the field with tents for campervans and cars.",
      ];

  return [
    {
      key: "food",
      label: "Food",
      Icon: FaUtensils,
      summary: "Meal locations and the key times for booked food during the rally weekend.",
      tab: "Venue",
      anchor: "meals",
      tabLabel: "Open meals section",
      sections: [
        {
          title: "Where food is served",
          rows: [
            "Breakfasts and booked evening meals are served in the Dining Hall.",
            "The Sunday evening BBQ is served on Yew Tree Avenue.",
            "Food, BBQ and polo shirt orders are now closed online.",
          ],
        },
        {
          title: "Meal times",
          rows: foodEvents.map(
            (event) =>
              `${event.dayLabel}: ${event.title} - ${event.time}, ${event.location}`
          ),
        },
      ],
    },
    {
      key: "rideouts",
      label: "Ride-outs",
      Icon: FaRoute,
      summary: "Departure times, meeting point and organisers for the rally ride-outs.",
      tab: "RideOuts",
      anchor: "ride-outs",
      tabLabel: "Open ride-outs",
      sections: [
        {
          title: "Departures",
          rows: rideEvents.map(
            (event) =>
              `${event.dayLabel}: ${event.title} - ${event.time}, ${event.location}`
          ),
        },
        {
          title: "Route notes",
          rows: (ridesData?.rides ?? []).map((ride) => {
            const departure = (ride.details ?? []).find(
              (detail) => detail.label === "Departure time"
            )?.value;
            const distance = (ride.details ?? []).find(
              (detail) => detail.label === "Distance"
            )?.value;

            return `${ride.title}: ${departure || "time listed on programme"} from the Camping Ground${
              distance ? `, ${distance}` : ""
            }.`;
          }),
        },
      ],
    },
    {
      key: "routes",
      label: "GPX routes",
      Icon: FaMapMarkedAlt,
      summary: "Downloadable GPX files, route maps and altitude profiles for the final routes.",
      tab: "RideOuts",
      anchor: "ride-outs",
      tabLabel: "View route maps",
      sections: [
        {
          title: "Downloads",
          rows: (ridesData?.rides ?? []).map((ride) => {
            const download = (ride.downloads ?? []).find((item) => item.href);
            return `${ride.title}: ${
              download?.label || "GPX download available in the ride-out section"
            }.`;
          }),
        },
        {
          title: "On-page route tools",
          rows: [
            "Each GPX route has a route map preview on the page.",
            "The route cards now include altitude profiles taken from the GPX elevation data.",
            ridesData?.satnavNote || "Routes are final and reflect the accurate rally route maps.",
          ],
        },
      ],
    },
    {
      key: "services",
      label: "Local services",
      Icon: FaGasPump,
      summary: "Fuel, toilets, showers, parking and nearby classic motorcycle help.",
      tab: "Extras",
      anchor: "local-services",
      tabLabel: "Open extras",
      sections: [
        {
          title: "Nearby services",
          rows: serviceRows,
        },
      ],
      actions: serviceItems.flatMap((item) =>
        (item.actions ?? []).map((action) => ({
          label: `${item.title}: ${action.label.replace(/^[^\w/]+/, "").trim()}`,
          uri: actionUri(action.uri),
        }))
      ),
    },
    {
      key: "arrival",
      label: "Arrival",
      Icon: FaInfoCircle,
      summary: "Where to arrive, how to check in, and the main venue address.",
      tab: "Venue",
      anchor: "getting-here",
      tabLabel: "Open arrival details",
      sections: [
        {
          title: "Check-in",
          rows: [
            "Please arrive at the camping field entrance first; rally reception will be based there.",
            "Camping field and rally reception: ///engage.soda.solve.",
            "Main site reference entrance: ///best.sparks.tuck.",
            "Venue address: Mount Kelly College, Parkwood Road, Tavistock, Devon, PL19 0HZ.",
          ],
        },
        {
          title: "Reception opening times",
          rows: receptionOpeningRows,
        },
      ],
      actions: [
        {
          label: "Camping field reception",
          uri: "https://what3words.com/engage.soda.solve",
        },
        {
          label: "Main site entrance",
          uri: "https://what3words.com/best.sparks.tuck",
        },
      ],
    },
    {
      key: "accommodation",
      label: "Stay",
      Icon: FaBed,
      summary: "Quick guide to camping, camper vans, rooms and on-site facilities.",
      tab: "Venue",
      anchor: "accommodation",
      tabLabel: "Open accommodation",
      sections: [
        {
          title: "Accommodation",
          rows: [
            "Camping is on the rugby field directly over the road from the main school buildings.",
            "Cars can park on the field next to tents; camper vans may also park there if ground conditions are suitable.",
            "Mount Kelly rooms are on site and are first come, first served.",
            "Dogs are welcome, but please contact us first so Mount Kelly can confirm case by case.",
          ],
        },
      ],
      actions: contactItems.flatMap((item) =>
        (item.actions ?? []).map((action) => ({
          label: action.label.replace(/^[^\w/]+/, "").trim(),
          uri: actionUri(action.uri),
        }))
      ),
    },
  ];
}

function QuickInfoModal({ item, onClose, onJumpToTab }) {
  useEffect(() => {
    if (!item) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [item, onClose]);

  if (!item) return null;

  const { Icon } = item;

  return (
    <div className="jp-quick-modal" role="presentation" onMouseDown={onClose}>
      <div
        className="jp-quick-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jp-quick-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="jp-quick-modal__close"
          onClick={onClose}
          aria-label="Close quick information"
        >
          <FaTimes aria-hidden="true" />
        </button>

        <div className="jp-quick-modal__header">
          <span className="jp-quick-modal__icon" aria-hidden="true">
            <Icon />
          </span>
          <div>
            <h2 id="jp-quick-modal-title">{item.label}</h2>
            <p>{item.summary}</p>
          </div>
        </div>

        <div className="jp-quick-modal__content">
          {(item.sections ?? []).map((section) => (
            <section key={section.title}>
              <h3>{section.title}</h3>
              <ul>
                {(section.rows ?? []).filter(Boolean).map((row) => (
                  <li key={row}>{row}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="jp-quick-modal__actions">
          {item.tab ? (
            <button
              type="button"
              onClick={() => onJumpToTab(item.tab, item.anchor)}
              className="jp-quick-modal__jump"
            >
              {item.tabLabel || "Open full section"}
            </button>
          ) : null}

          {(item.actions ?? []).slice(0, 6).map((action) => (
            <a key={`${action.label}-${action.uri}`} href={action.uri} target="_blank" rel="noreferrer">
              {action.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickInfoStrip({ items, activeKey, onOpen }) {
  return (
    <section className="jp-quick-info" aria-label="Quick rally information">
      <div className="jp-quick-info__inner">
        <p>Quick answers</p>
        <div className="jp-quick-info__buttons">
          {items.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              className={activeKey === key ? "is-active" : ""}
              onClick={() => onOpen(key)}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function RallyTabTitle({ tab }) {
  const details = rallyTabTitles[tab] ?? rallyTabTitles.Venue;

  return (
    <header className="jp-tab-title" aria-live="polite">
      <img src={wingLeft} alt="" aria-hidden="true" className="jp-tab-title__wing jp-tab-title__wing--left" />
      <div className="jp-tab-title__copy">
        <h2>{details.title}</h2>
        <p>{details.subtitle}</p>
      </div>
      <img src={wingRight} alt="" aria-hidden="true" className="jp-tab-title__wing jp-tab-title__wing--right" />
    </header>
  );
}

function JampotRally({ onNavigate }) {
  const [activeTab, setActiveTab] = useState("Venue");
  const [activeQuickInfoKey, setActiveQuickInfoKey] = useState(null);

const [lightbox, setLightbox] = useState({ open: false, src: "", alt: "" });

const openLightbox = (src, alt) => setLightbox({ open: true, src, alt });
const closeLightbox = () => setLightbox({ open: false, src: "", alt: "" });

  const [programme, setProgramme] = useState(null);
  const [programmeLoading, setProgrammeLoading] = useState(true);
  const [programmeError, setProgrammeError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadProgramme = async () => {
      try {
        setProgrammeLoading(true);
        setProgrammeError("");

        const res = await fetch("/rallies/2026/programme.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`Programme fetch failed: ${res.status}`);

        const json = await res.json();
        if (!cancelled) setProgramme(json);
      } catch (err) {
        if (!cancelled) setProgrammeError(err?.message ?? "Failed to load programme");
      } finally {
        if (!cancelled) setProgrammeLoading(false);
      }
    };

    loadProgramme();
    return () => {
      cancelled = true;
    };
  }, []);

  const [ridesData, setRidesData] = useState(null);
  const [ridesLoading, setRidesLoading] = useState(true);
  const [ridesError, setRidesError] = useState("");
  const [rallyInfo, setRallyInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadRides = async () => {
      try {
        setRidesLoading(true);
        setRidesError("");

        const res = await fetch("/rallies/2026/rides.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`Rides fetch failed: ${res.status}`);

        const json = await res.json();
        if (!cancelled) setRidesData(json);
      } catch (err) {
        if (!cancelled) setRidesError(err?.message ?? "Failed to load ride-outs");
      } finally {
        if (!cancelled) setRidesLoading(false);
      }
    };

    loadRides();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadRallyInfo = async () => {
      try {
        const res = await fetch("/rallies/2026/information.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`Information fetch failed: ${res.status}`);

        const json = await res.json();
        if (!cancelled) setRallyInfo(json);
      } catch {
        if (!cancelled) setRallyInfo(null);
      }
    };

    loadRallyInfo();
    return () => {
      cancelled = true;
    };
  }, []);

  const quickInfoItems = buildQuickInfoItems({ programme, ridesData, rallyInfo });
  const activeQuickInfo = quickInfoItems.find((item) => item.key === activeQuickInfoKey) || null;

  const handleQuickInfoJump = (tab, anchor = "overview") => {
    setActiveTab(tab);
    setActiveQuickInfoKey(null);
    window.setTimeout(() => {
      document.getElementById(anchor)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Venue":
        return (
          <>
            <p>
              The rally weekend runs from <strong>Thursday 13th August to Monday 17th August 2026</strong>,
              with early arrivals welcome from Thursday. Camping and accommodation options are available on
              site. Ride-outs will take in some of the best roads across <strong>Dartmoor</strong>, the{" "}
              <strong>North Cornwall coast</strong>, and the <strong>Rame Peninsula</strong>.
            </p>

            <PhotoStrip
              images={[
                { src: braunton1, alt: "Braunton coast" },
                { src: tintagel1, alt: "Tintagel cliffs" },
                { src: tintagel2, alt: "Tintagel sea window" },
              ]}
            />

            <img src={map} alt="Mount Kelly Campus Map" className="jampot-map-image" />

            <p>
              This page will be updated as details are finalised — or come along to our section meetings if
              you’d like to get involved with the planning.
            </p>

            {/* Key Info */}
            <section className="jp-section" id="key-info">
              <h2>Key Information</h2>
              <ul>
                <li>
                  <strong>Dates:</strong> Thursday <strong>13th August</strong> to Monday{" "}
                  <strong>17th August 2026</strong> (early arrival from Thursday)
                </li>
                <li>
                  <strong>Venue:</strong> <strong>Mount Kelly</strong>, Tavistock (Devon)
                </li>
                <li>
                  <strong>Address:</strong> Mount Kelly College, Parkwood Road, Tavistock, Devon, PL19 0HZ
                </li>
                <li>
                  <strong>Arrival:</strong> Please arrive via the camping field entrance for rally reception
                </li>
                <li>
                  <strong>Reception:</strong> Thursday 12pm-5pm, Friday 9am-5pm, Saturday 9am-5pm,
                  Sunday 9am-12pm, Monday closed
                </li>
                <li>
                  <strong>Camping:</strong> Available on-site (rugby field)
                </li>
                <li>
                  <strong>Rooms:</strong> Available on-site (capacity up to 60 in 29 rooms) —{" "}
                  <em>first come, first served</em>
                </li>
                <li>
                  <strong>Entertainment &amp; Bar:</strong> Main Hall (run by Mount Kelly staff)
                </li>
              </ul>
            </section>

            <GettingHerePanel />

<div className="jp-info-widget jp-info-widget-dogs" role="note" aria-label="Dog information">
  <div className="jp-info-widget-icon" aria-hidden="true">🐾</div>

  <div className="jp-info-widget-body">
    <h3>Dogs welcome</h3>

    <p>
      <strong>Mount Kelly is a dog-friendly site</strong>, so dogs are welcome at the rally. However,
      we’ve been asked to confirm dogs with Mount Kelly <strong>case by case in advance</strong>.
      Please contact us before bringing your dog.
    </p>

    <a
      className="jp-email-button"
      href="mailto:southwest@jampot.com?subject=Jampot Rally 2026 – Bringing a Dog"
    >
      Email to check about bringing a dog
    </a>
  </div>
</div>

            {/* Accommodation */}
            <section className="jp-section" id="accommodation">
              <h2>Accommodation</h2>
              <BoardingRoomPriceNotice />
              <p>
                Mount Kelly can accommodate <strong>up to 60 people</strong> in rooms on site. Rooms are{" "}
                <strong>not en-suite</strong>. Room bookings will be{" "}
                <strong>
                  first come, first served. We are booking the whole of the accomodation in Coutenay House.
                  When the booking form is open, you will be able to select a room from a floor plan
                </strong>
                .
              </p>
              <ul>
                <li>
                  <strong>Single rooms:</strong>  9
                </li>
                <li>
                  <strong>Twin rooms:</strong>  10
                </li>
                <li>
                  <strong>Triple rooms:</strong> 9
                </li>
                <li>
                  <strong>Quadruple rooms:</strong> 1
                </li>
              </ul>
              <div className="jp-floorplans">
  <h3>Courtenay House floor plans</h3>

  <div className="jp-floorplans-row">
    <button
      type="button"
      className="jp-floorplan-thumb"
      onClick={() => openLightbox(floorGround, "Courtenay House – Ground floor plan")}
    >
      <img src={floorGround} alt="Courtenay House – Ground floor plan" loading="lazy" />
      <span>Ground</span>
    </button>

    <button
      type="button"
      className="jp-floorplan-thumb"
      onClick={() => openLightbox(floor1, "Courtenay House – Floor 1 plan")}
    >
      <img src={floor1} alt="Courtenay House – Floor 1 plan" loading="lazy" />
      <span>Floor 1</span>
    </button>

    <button
      type="button"
      className="jp-floorplan-thumb"
      onClick={() => openLightbox(floor2, "Courtenay House – Floor 2 plan")}
    >
      <img src={floor2} alt="Courtenay House – Floor 2 plan" loading="lazy" />
      <span>Floor 2</span>
    </button>
  </div>

  <p className="note-text">
    Tap a floor plan to enlarge.
  </p>
</div>
              <p>
                If we exceed capacity, or if you specifically want an en-suite, Mount Kelly can offer a
                discounted overflow option locally — including the <strong>Bedford Hotel</strong>.
              </p>
            </section>

            {/* Camping & Campers */}
            <section className="jp-section" id="camping">
              <h2>Camping &amp; Camper Vans</h2>
              <p>
                Camping will be on the <strong>rugby field directly over the road</strong> from the main
                school buildings. Mount Kelly have confirmed we can now park our <strong>cars</strong>{" "}
                next to our tents on the field — and <strong>camper vans may also be able to park on the field</strong>{" "}
                <em>(subject to favourable dry conditions)</em>.
              </p>

              <p>
                If ground conditions aren’t suitable, the{" "}
                <strong>hard surfaced area opposite the swimming centre</strong> will be used as an
                overflow.
              </p>

              <ul>
                <li>
                  <strong>Tents:</strong> pitched on the rugby field
                </li>
                <li>
                  <strong>Cars:</strong> parking on the field next to your tents
                </li>
                <li>
                  <strong>Camper vans:</strong> may park on the field{" "}
                  <em>(subject to favourable dry conditions)</em>, with overflow on the hard surfaced area
                  opposite the swimming centre
                </li>
              </ul>
            </section>

          {/* Meals & Prices */}
<section className="jp-section" id="meals">
  <h2>Meals</h2>

  <p>
    Meals will be served in the <strong>Dining Hall</strong>. There’s seating for around{" "}
    <strong>140 inside</strong>, with the option to open the side doors and put out additional
    seating outside.
  </p>

  <RallyMenu />


</section>

            {/* Hall, Bar & Entertainment */}
            <section className="jp-section" id="hall">
              <h2>Main Hall, Bar &amp; Entertainment</h2>
              <p>
                We can hire the <strong>Main Hall</strong> instead of a marquee, and we unanimously agreed
                the hall is the better option. It has capacity for around <strong>120</strong> with
                cabaret-style round tables, plus a <strong>stage</strong> for live music.
              </p>
              <ul>
                <li>
                  <strong>Bar:</strong> at the back of the hall, with additional seating
                </li>
                <li>
                  <strong>Mezzanine:</strong> above the bar with even more seating
                </li>
                <li>
                  <strong>Staffing:</strong> Mount Kelly will organise and man the bar
                </li>
                <li>
                  <strong>Live music:</strong> live band on <strong>Friday</strong> and <strong>Saturday</strong>
                </li>
              </ul>
            </section>

            {/* Venue Highlights */}
            <section className="jp-section" id="venue-highlights">
              <h2>Venue Highlights</h2>

              <h3>Yew Tree Avenue (Autojumble + Sunday BBQ)</h3>
              <p>
                The yew tree avenue is apparently the <strong>longest avenue of yew trees in Europe</strong>{" "}
                — and it’s genuinely stunning. We can host an <strong>autojumble</strong> there, and Mount
                Kelly can put on a <strong>BBQ on Sunday evening (7pm)</strong>.
              </p>

              <h3>Parade Ground (Bike Display + Photos)</h3>
              <p>
                The parade ground is a big gravelled area in front of the school — perfect for a mass bike
                display and photos during the weekend.
              </p>

              <h3>AGM</h3>
              <p>
                The <strong>AGM is planned for Saturday at 5pm</strong>. The exact room/location on site will
                be confirmed once final arrangements are in place.
              </p>
            </section>

            <img src={mtkelly} alt="Mount Kelly" className="jampot-map-image" />
          </>
        );

      case "RideOuts":
        return (
          <section className="jp-section" id="ride-outs">
            <h2>Ride Outs</h2>

            <PhotoStrip
              images={[
                { src: dartmoor1, alt: "Dartmoor scenery" },
                { src: braunton2, alt: "Braunton beach" },
                { src: dartmoor2, alt: "Dartmoor scenery" },
              ]}
            />

            {ridesLoading && <p>Loading ride-outs…</p>}

            {!ridesLoading && ridesError && (
              <p style={{ color: "salmon" }}>Couldn’t load ride-outs. ({ridesError})</p>
            )}

            {!ridesLoading && !ridesError && ridesData && (
              <>
                <p>{ridesData.intro}</p>

                {(ridesData.rides ?? []).map((ride, idx) => (
                  <div key={idx} className={"rideout-block" + (ride.isMain ? " main-rideout" : "")}>
                    <h3>{ride.title}</h3>
                    {ride.meta && <p className="rideout-meta">{ride.meta}</p>}

                    <ul>
                      {(ride.details ?? []).map((d, i) => (
                        <li key={i}>
                          <strong>{d.label}:</strong> {d.value}
                        </li>
                      ))}
                    </ul>

                    {ride.description && <p>{ride.description}</p>}
                    <RideRouteMap ride={ride} />
                  </div>
                ))}

                {ridesData.satnavNote && <p className="rideout-note">{ridesData.satnavNote}</p>}
                {ridesData.note && <p className="rideout-note">{ridesData.note}</p>}
              </>
            )}
          </section>
        );

      case "Events":
        return (
          <section className="jp-section" id="events">
            <h2>Events Programme</h2>

            <p>
              The programme runs across the long weekend (Thursday arrivals through to Monday pack-up).
              Timings and details will be confirmed closer to the event.
            </p>

            {programmeLoading && <p>Loading programme…</p>}

            {!programmeLoading && programmeError && (
              <p style={{ color: "salmon" }}>Couldn’t load programme data. ({programmeError})</p>
            )}

            {!programmeLoading && !programmeError && programme?.days?.length > 0 && (
              <>
                {programme.days.map((day) => {
                  const ent = day.entertainment;

                  return (
                    <div
                      key={day.date}
                      className={
                        "event-day-card" +
                        (day.label?.toLowerCase().includes("saturday") ||
                        day.label?.toLowerCase().includes("sunday") ||
                        day.label?.toLowerCase().includes("friday")
                          ? " main-event-day"
                          : "")
                      }
                    >
                      <h3>{day.label}</h3>

<ul className="event-list">
  {(day.events ?? []).map((evt, idx) => (
    <li key={idx} className="event-card">

      <div className="event-time">
        {evt.time}
      </div>

      <div className="event-body">
        <div className="event-title">
          {evt.title}
        </div>

        <div className="event-meta">
          {evt.location}
          
        </div>

        {evt.description ? (
          <p className="event-description">{evt.description}</p>
        ) : null}
      </div>

    </li>
  ))}
</ul>

                      {ent && (
                        <div className="event-entertainment-card">
                          <div className="event-entertainment-media">
                            {ent.image && (
                              <img
                                src={ent.image}
                                alt={ent.title || "Entertainment"}
                                className="event-entertainment-image"
                                loading="lazy"
                              />
                            )}
                          </div>

                          <div className="event-entertainment-body">
                            <h4 className="event-entertainment-title">
                              {ent.title || "Evening entertainment"}
                            </h4>

                            {(ent.time || ent.location) && (
                              <p className="event-entertainment-meta">
                                {ent.time ? (
                                  <>
                                    <strong>Time:</strong> {ent.time}
                                  </>
                                ) : null}
                                {ent.time && ent.location ? " • " : null}
                                {ent.location ? (
                                  <>
                                    <strong>Location:</strong> {ent.location}
                                  </>
                                ) : null}
                              </p>
                            )}

                            {ent.description && (
                              <p className="event-entertainment-desc">{ent.description}</p>
                            )}

                            {ent.link && (
                              <p className="event-entertainment-link">
                                <a href={ent.link} target="_blank" rel="noreferrer">
                                  More info →
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                <p className="event-note">
                  A full timetable with confirmed times will be published once all details are finalised.
                </p>
              </>
            )}
          </section>
        );

      case "Extras":
        return (
          <>
            {rallyInfo?.sections?.length ? (
              <section className="jp-section" id="local-services">
                <h2>Useful Contacts &amp; Local Services</h2>

                <div className="jp-local-info-list">
                  {rallyInfo.sections.map((section) => (
                    <div key={section.title} className="jp-local-info-group">
                      <h3>{section.title}</h3>

                      <div className="jp-local-info-grid">
                        {(section.items ?? []).map((item) => (
                          <article key={`${section.title}-${item.title}-${item.detail}`} className="jp-local-info-card">
                            <h4>{item.title}</h4>
                            <p>{item.detail}</p>

                            {(item.actions ?? []).length > 0 ? (
                              <div className="jp-local-info-actions">
                                {item.actions.map((action) => (
                                  <a
                                    key={`${item.title}-${action.label}`}
                                    href={actionUri(action.uri)}
                                    target={action.uri?.startsWith("tel:") ? undefined : "_blank"}
                                    rel={action.uri?.startsWith("tel:") ? undefined : "noreferrer"}
                                  >
                                    {action.label}
                                  </a>
                                ))}
                              </div>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

          <section className="jp-section" id="extras">
            <h2>Jampot Rally Companion App</h2>

            <p>
              We’ll be providing a <strong>Jampot Rally companion app</strong> to help you get the most out
              of the weekend. It brings together the programme, ride-outs and key site information in one
              place.
            </p>

            <p>
              The app includes <strong>live information</strong>, so if timings change, details are
              updated, or announcements need to be shared, you’ll see them straight away. That means you’re
              always looking at the <em>latest</em> version — no out-of-date printouts or guesswork.
            </p>

            <p>
              It’s completely optional, but especially handy when you’re on the move around the site or out
              on a ride-out.
            </p>

            <div className="jp-app-downloads" aria-label="Companion app downloads">
              <a
                className="jp-app-download-card"
                href={iosAppStoreUrl}
                target="_blank"
                rel="noreferrer"
              >
                <span>iPhone &amp; iPad</span>
                <strong>Download on the App Store</strong>
                <small>Free official Jampot Rally app</small>
              </a>

              {androidApkDownloadUrl ? (
                <a
                  className="jp-app-download-card"
                  href={androidApkDownloadUrl}
                  download="jampot-rally-android.apk"
                >
                  <span>Android</span>
                  <strong>Download APK</strong>
                  <small>Direct install file for Android devices</small>
                </a>
              ) : (
                <div className="jp-app-download-card jp-app-download-card--pending" aria-disabled="true">
                  <span>Android</span>
                  <strong>APK download coming shortly</strong>
                  <small>The Android install file will be added here when available.</small>
                </div>
              )}
            </div>

            <img src={appPreview} alt="Jampot Rally companion app preview" className="jampot-map-image" />
          </section>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="jampot2026-content">
      <div className="jp-rally-shell">
        <section className="jp-hero" style={{ backgroundImage: `url(${heroimage})` }}>
          <div className="jp-hero-inner">
            <div className="jp-hero-copy">
              <p className="jp-hero-eyebrow">Devon &amp; Cornwall Section</p>
              <h1>Jampot Rally 2026</h1>
              <h2>Hosted By Devon &amp; Cornwall</h2>
              <p className="jp-hero-intro">
                The clubs annual rally weekend of great riding, good company and proper hospitality.
              </p>
              <p className="jp-hero-script">Tavistock.</p>
            </div>

            <div className="jp-hero-badge-wrap">
              <img src={rallyBadge} alt="Jampot Rally 2026 badge" className="jp-hero-badge" />
            </div>
          </div>
        </section>

        <QuickInfoStrip
          items={quickInfoItems}
          activeKey={activeQuickInfoKey}
          onOpen={setActiveQuickInfoKey}
        />

        <div className="scroll-sections text-content">
          <section className="jp-paper" id="overview">
            <div className="jp-paper-top">
              <div className="jp-booking-cta">
                {bookingOpen ? (
                  <div className="jp-booking-actions">
                    <div className="jp-food-closed-notice">
                      Food, BBQ and polo shirt orders are now closed. You can still book rally attendance and accommodation.
                    </div>

                    <BoardingRoomPriceNotice compact />

                    <button
                      className="jp-booking-button active"
                      onClick={() => onNavigate("/booking")}
                    >
                      <span className="jp-booking-eyebrow">Jampot Rally 2026</span>
                      <span className="jp-booking-main">Book Your Place</span>
                      <span className="jp-booking-sub">13–17 August • Mount Kelly, Tavistock</span>
                    </button>

                    <button
                      type="button"
                      className="jp-booking-edit-button"
                      onClick={() => onNavigate("/managebooking")}
                    >
                      Edit existing booking
                    </button>
                  </div>
                ) : (
                  <button className="jp-booking-button inactive" disabled>
                    <span className="jp-booking-eyebrow">Jampot Rally 2026</span>
                    <span className="jp-booking-main">Bookings are now closed</span>
                    <span className="jp-booking-sub">The rally is underway • Please check in at reception</span>
                  </button>
                )}
              </div>

              <RallyStatsRow />
            </div>

            <p className="jp-overview-intro">
              The Devon &amp; Cornwall section is proud to be hosting the{" "}
              <strong>Jampot Rally 2026</strong> at the stunning grounds of{" "}
              <strong>Mount Kelly in Tavistock</strong>.
            </p>

            <section className="jp-deadline-panel" aria-labelledby="jp-deadline-title">
              <div className="jp-deadline-heading">
                <p className="jp-deadline-eyebrow">Booking deadlines</p>
                <h2 id="jp-deadline-title">Important ordering dates</h2>
              </div>

              <div className="jp-deadline-grid">
                <div className="jp-deadline-card">
                  <span className="jp-deadline-date">Friday 17th July 2026</span>
                  <p>
                    Cutoff for ordering food with bookings. Bookings can still be made after this date,
                    but food orders will no longer be taken.
                  </p>
                </div>

                <div className="jp-deadline-card">
                  <span className="jp-deadline-date">Wednesday 5th August 2026</span>
                  <p>Last order date for rally shirts.</p>
                </div>
              </div>
            </section>

            <section className="jp-shirts-panel" aria-labelledby="jp-shirts-title">
              <div className="jp-shirts-copy">
                <p className="jp-deadline-eyebrow">Rally shirts</p>
                <h2 id="jp-shirts-title">2026 rally shirts</h2>
                <p>
                  The <strong>blue polo shirt</strong> is the shirt supplied where one has been ordered.
                  The <strong>hoodie</strong> and <strong>white T-shirt</strong> will be available to buy
                  on the day at the rally, in limited numbers and sizes.
                </p>
              </div>

              <figure className="jp-shirts-figure">
                <img src={rallyShirts} alt="Jampot Rally 2026 hoodie, white T-shirt and blue polo shirt" />
              </figure>
            </section>

            <p className="jp-tabs-label">Choose a rally section</p>

            <div className="bikes-tabs" role="tablist" aria-label="Jampot Rally sections">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "Venue"}
                className={"bikes-tab-button" + (activeTab === "Venue" ? " active" : "")}
                onClick={() => setActiveTab("Venue")}
              >
                Venue
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "Events"}
                className={"bikes-tab-button" + (activeTab === "Events" ? " active" : "")}
                onClick={() => setActiveTab("Events")}
              >
                Events Programme
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "RideOuts"}
                className={"bikes-tab-button" + (activeTab === "RideOuts" ? " active" : "")}
                onClick={() => setActiveTab("RideOuts")}
              >
                Ride Outs
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "Extras"}
                className={"bikes-tab-button" + (activeTab === "Extras" ? " active" : "")}
                onClick={() => setActiveTab("Extras")}
              >
                Extras
              </button>
            </div>

            <RallyTabTitle tab={activeTab} />

            <div className="bikes-tab-panel">{renderTabContent()}</div>
          </section>
        </div>
      </div>

      <Lightbox
        open={lightbox.open}
        src={lightbox.src}
        alt={lightbox.alt}
        onClose={closeLightbox}
      />

      <QuickInfoModal
        item={activeQuickInfo}
        onClose={() => setActiveQuickInfoKey(null)}
        onJumpToTab={handleQuickInfoJump}
      />
    </div>
  );
}

export default JampotRally;
